import {CSSProperties, MouseEvent, RefObject, useEffect, useState} from 'react';
import clsx from 'clsx';
import {t} from 'i18next';
import {ReactComponent as ArrowIncreaseIcon} from '../../../../../assets/images/arrow-increase.svg';
import ArrButton from '../../../../common/arr-button/ArrButton';
import './BackToTopButton.scss';

export interface BackToTopButtonProps {
  scrollContainerRef: RefObject<HTMLElement | null>;
  threshold?: number;
  onScrollToTop?: () => void;
  className?: string;
  testId?: string;
}

const EDGE_OFFSET_PX = 16;
const BOTTOM_OFFSET_PX = 8;

const isElementScrollable = (element: HTMLElement) => {
  const {overflowY} = window.getComputedStyle(element);
  const canScroll =
    overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  return canScroll && element.scrollHeight > element.clientHeight + 1;
};

const resolveScroller = (start: HTMLElement): HTMLElement => {
  let node: HTMLElement | null = start;
  while (node) {
    if (isElementScrollable(node)) {
      return node;
    }
    node = node.parentElement;
  }

  const content = document.querySelector(
    '.app-container .main-area .content'
  ) as HTMLElement | null;
  if (content && content.scrollHeight > content.clientHeight + 1) {
    return content;
  }

  const mainArea = document.querySelector(
    '.app-container .main-area'
  ) as HTMLElement | null;
  if (mainArea && mainArea.scrollHeight > mainArea.clientHeight + 1) {
    return mainArea;
  }

  return start;
};

const resolvePositionElement = (start: HTMLElement): HTMLElement => {
  const contentPanel = start.closest('.content-panel');
  return contentPanel instanceof HTMLElement ? contentPanel : start;
};

const scrollElementToTop = (element: HTMLElement) => {
  if (typeof element.scrollTo === 'function') {
    element.scrollTo({top: 0, behavior: 'smooth'});
    return;
  }

  element.scrollTop = 0;
};

const isWindowScrolled = (threshold: number) =>
  window.scrollY > threshold ||
  document.documentElement.scrollTop > threshold ||
  document.body.scrollTop > threshold;

const scrollWindowToTop = () => {
  if (typeof window.scrollTo === 'function') {
    window.scrollTo({top: 0, behavior: 'smooth'});
    return;
  }

  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

const BackToTopButton = ({
  scrollContainerRef,
  threshold = 0,
  onScrollToTop,
  className,
  testId = 'back-to-top-button',
}: BackToTopButtonProps) => {
  const [visible, setVisible] = useState(false);
  const [offset, setOffset] = useState({
    bottom: BOTTOM_OFFSET_PX,
    right: EDGE_OFFSET_PX,
  });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    const update = () => {
      const scroller = resolveScroller(container);
      setVisible(
        container.scrollTop > threshold ||
          scroller.scrollTop > threshold ||
          isWindowScrolled(threshold)
      );

      const rect = resolvePositionElement(container).getBoundingClientRect();
      setOffset({
        bottom: Math.max(
          BOTTOM_OFFSET_PX,
          window.innerHeight -
            Math.min(rect.bottom, window.innerHeight) +
            BOTTOM_OFFSET_PX
        ),
        right: Math.max(
          EDGE_OFFSET_PX,
          window.innerWidth - rect.right + EDGE_OFFSET_PX
        ),
      });
    };

    update();
    container.addEventListener('scroll', update, {passive: true});
    document.addEventListener('scroll', update, {passive: true, capture: true});
    window.addEventListener('scroll', update, {passive: true});
    window.addEventListener('resize', update);

    return () => {
      container.removeEventListener('scroll', update);
      document.removeEventListener('scroll', update, {capture: true});
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [scrollContainerRef, threshold]);

  if (!visible) {
    return null;
  }

  const label = t('ai.review.protocolWizard.backToTop');

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onScrollToTop?.();

    const container = scrollContainerRef.current;
    if (container) {
      scrollElementToTop(container);
      const scroller = resolveScroller(container);
      if (scroller !== container) {
        scrollElementToTop(scroller);
      }
    }

    if (isWindowScrolled(0)) {
      scrollWindowToTop();
    }
  };

  const positionStyle: CSSProperties = {
    bottom: offset.bottom,
    right: offset.right,
  };

  return (
    <ArrButton
      variant="primary"
      size="medium"
      startIcon={<ArrowIncreaseIcon aria-hidden="true" />}
      className={clsx('back-to-top-button', className)}
      dataTestId={testId}
      aria-label={label}
      type="button"
      style={positionStyle}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={handleClick}
    >
      <span className="back-to-top-button__label">{label}</span>
    </ArrButton>
  );
};

export default BackToTopButton;
